import { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./FAQAccordion.css";

/**
 * FAQAccordion — accessible accordion for FAQ content.
 *
 * Keyboard: Enter/Space toggle the focused question.
 * ARIA: uses aria-expanded + aria-controls for screen readers.
 */
export default function FAQAccordion({ items }) {
  const { t } = useTranslation("blog");
  const [openIndex, setOpenIndex] = useState(0);

  const toggle = useCallback(
    (i) => setOpenIndex((prev) => (prev === i ? -1 : i)),
    []
  );

  return (
    <section className="faq-section" aria-labelledby="faq-heading">
      <div className="section-eyebrow" aria-hidden="true">
        <span className="eyebrow-line" />
        <span>{t("faq.eyebrow")}</span>
      </div>
      <h2 id="faq-heading" className="faq-title">
        {t("faq.title0")} <em>{t("faq.title1")}</em>
      </h2>

      <dl className="faq-list">
        {items.map(({ q, a }, i) => {
          const panelId  = `faq-panel-${i}`;
          const buttonId = `faq-btn-${i}`;
          const isOpen   = openIndex === i;

          return (
            <div
              key={q || i}
              className={`faq-item${isOpen ? " faq-item--open" : ""}`}
            >
              <dt>
                <button
                  id={buttonId}
                  className="faq-q"
                  onClick={() => toggle(i)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <span>{q}</span>
                  <span className="faq-icon" aria-hidden="true">
                    {isOpen ? "−" : "+"}
                  </span>
                </button>
              </dt>
              <dd
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                className="faq-a-wrap"
                hidden={!isOpen}
              >
                <p className="faq-a">{a}</p>
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

FAQAccordion.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      q: PropTypes.string.isRequired,
      a: PropTypes.string.isRequired,
    })
  ).isRequired,
};
